import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faEyeSlash,
  faCircleNotch,
  faCheckCircle,
  faArrowLeft,
} from "@fortawesome/free-solid-svg-icons";
import "../styles/Login.css";
import prophecyLogo2 from "../Assets/images/prophecy-logo2.png";
import prophecyLogo from "../Assets/images/prophecy-logo.png";
import projectProgressImg from "../Assets/images/project_progress_showcase.jpg";
import BASE_URL from "../../url";

const ForgotPassword = () => {
  const [step, setStep] = useState(1); // 1: Request, 2: Reset, 3: Success
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [userInfo, setUserInfo] = useState(null);
  const [tokenVerified, setTokenVerified] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const showcaseSlides = [
    {
      title: "Secure Account Recovery",
      description:
        "Reset your password with encrypted verification to restore fast, secure access to your enterprise workspace.",
    },
    {
      title: "Enterprise Pipeline Access",
      description:
        "Quickly regain access to candidate submissions, timesheets, and recruitment analytics seamlessly.",
    },
    {
      title: "Protected Data & Analytics",
      description:
        "Industry-standard authentication ensuring your enterprise tools and applicant data stay safe and compliant.",
    },
  ];

  // Auto-rotate showcase slides
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % showcaseSlides.length);
    }, 5500);
    return () => clearInterval(timer);
  }, [showcaseSlides.length]);

  // Check for token in URL
  useEffect(() => {
    const token = searchParams.get("token");
    if (token) {
      verifyToken(token);
    } else {
      // No token in URL, show email input form
      setStep(1);
      setTokenVerified(true);
    }
  }, [searchParams]);

  const verifyToken = async (token) => {
    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${BASE_URL}/api/auth/verify-reset-token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Invalid or expired token");
      }

      setUserInfo(data);
      setStep(2); // Move to reset password step
      setTokenVerified(true);
    } catch (error) {
      console.error("Token verification error:", error);
      setError(error.message);
      setStep(1); // Go back to email input if token is invalid
      setTokenVerified(true);
    }

    setLoading(false);
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${BASE_URL}/api/auth/request-reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.toLowerCase().trim() }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to send reset email");
      }

      setSuccess(data.message || "Password reset link has been sent to your email.");
      setStep(3); // Move to success step
    } catch (error) {
      console.error("Request reset error:", error);
      setError(error.message);
    }

    setLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    // Validation
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      setLoading(false);
      return;
    }

    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters long");
      setLoading(false);
      return;
    }

    const token = searchParams.get("token");

    try {
      const response = await fetch(`${BASE_URL}/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          newPassword,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to reset password");
      }

      setSuccess("Password has been reset successfully! Redirecting to login...");
      setStep(3); // Move to success step

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate("/");
      }, 3000);
    } catch (error) {
      console.error("Reset password error:", error);
      setError(error.message);
    }

    setLoading(false);
  };

  const toggleNewPasswordVisibility = () => setShowNewPassword((prev) => !prev);
  const toggleConfirmPasswordVisibility = () => setShowConfirmPassword((prev) => !prev);

  const handleBackToLogin = () => {
    navigate("/");
  };

  const handleResetAnother = () => {
    setStep(1);
    setEmail("");
    setNewPassword("");
    setConfirmPassword("");
    setError("");
    setSuccess("");
    setUserInfo(null);
    // Clear URL parameters
    window.history.replaceState({}, document.title, window.location.pathname);
  };

  // Show loading while verifying token
  if (!tokenVerified) {
    return (
      <div className="login-page-container">
        <div className="login-topbar-brand">
          <Link to="/" style={{ display: "flex", alignItems: "center", gap: "6px", textDecoration: "none" }}>
            <img src={prophecyLogo2} alt="Logo" className="topbar-logo-icon" />
            <img src={prophecyLogo} alt="Prophecy Technologies" className="topbar-logo-text" />
          </Link>
        </div>

        <div className="login-main-card" style={{ maxWidth: "560px", minHeight: "380px" }}>
          <div className="login-form-side" style={{ width: "100%", padding: "48px 36px" }}>
            <div className="login-form-content" style={{ textAlign: "center" }}>
              <FontAwesomeIcon
                icon={faCircleNotch}
                spin
                style={{ fontSize: "44px", color: "#3b5bfd", marginBottom: "20px" }}
              />
              <h2 className="login-main-title" style={{ fontSize: "22px", marginBottom: "8px" }}>
                Verifying Reset Link...
              </h2>
              <p className="signup-desc-text">Please wait while we validate your security token.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page-container">
      {/* Top Left White Brand Logo on Blue Background */}
      <div className="login-topbar-brand">
        <Link to="/" style={{ display: "flex", alignItems: "center", gap: "6px", textDecoration: "none" }}>
          <img src={prophecyLogo2} alt="Logo" className="topbar-logo-icon" />
          <img src={prophecyLogo} alt="Prophecy Technologies" className="topbar-logo-text" />
        </Link>
      </div>

      <div className="login-main-card">
        {/* Left Side: Reset Password Form Area */}
        <div className="login-form-side">
          <div className="login-form-content">
            {/* Back to Login Link */}
            <Link to="/" className="login-back-link">
              <FontAwesomeIcon icon={faArrowLeft} />
              <span>Back to Login</span>
            </Link>

            {/* Step 1: Request Password Reset */}
            {step === 1 && (
              <>
                <h1 className="login-main-title">Reset Password</h1>
                <p className="signup-desc-text">
                  Enter your email address and we'll send you a link to reset your password.
                </p>

                <form onSubmit={handleRequestReset} className="login-actual-form">
                  <div className="login-field-box">
                    <label className="login-field-label">Email address</label>
                    <input
                      type="email"
                      className="login-field-input"
                      placeholder="name@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoComplete="email"
                    />
                  </div>

                  {error && <div className="login-card-error">{error}</div>}

                  <button
                    type="submit"
                    className={`login-action-btn ${loading ? "loading" : ""}`}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <FontAwesomeIcon icon={faCircleNotch} spin style={{ marginRight: "8px" }} />
                        Sending reset link...
                      </>
                    ) : (
                      "Send Reset Link"
                    )}
                  </button>

                  <p className="login-bottom-toggle-text">
                    Remember your password ?{" "}
                    <Link to="/" className="login-toggle-action-link">
                      Login
                    </Link>
                  </p>
                </form>
              </>
            )}

            {/* Step 2: Reset Password (with verified token) */}
            {step === 2 && userInfo && (
              <>
                <h1 className="login-main-title">Set New Password</h1>
                <p className="signup-desc-text">
                  Hello <strong>{userInfo.firstName || userInfo.username}</strong>, please enter your new password below.
                </p>

                <form onSubmit={handleResetPassword} className="login-actual-form">
                  <div className="login-field-box">
                    <label className="login-field-label">New Password</label>
                    <div className="login-input-with-icon">
                      <input
                        type={showNewPassword ? "text" : "password"}
                        className="login-field-input"
                        placeholder="Enter new password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        className="login-eye-toggle"
                        onClick={toggleNewPasswordVisibility}
                        tabIndex="-1"
                        aria-label="Toggle password visibility"
                      >
                        <FontAwesomeIcon icon={showNewPassword ? faEyeSlash : faEye} />
                      </button>
                    </div>
                  </div>

                  <div className="login-field-box">
                    <label className="login-field-label">Confirm New Password</label>
                    <div className="login-input-with-icon">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        className="login-field-input"
                        placeholder="Confirm new password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        className="login-eye-toggle"
                        onClick={toggleConfirmPasswordVisibility}
                        tabIndex="-1"
                        aria-label="Toggle password visibility"
                      >
                        <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                      </button>
                    </div>
                  </div>

                  {error && <div className="login-card-error">{error}</div>}

                  <button
                    type="submit"
                    className={`login-action-btn ${loading ? "loading" : ""}`}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <FontAwesomeIcon icon={faCircleNotch} spin style={{ marginRight: "8px" }} />
                        Resetting password...
                      </>
                    ) : (
                      "Reset Password"
                    )}
                  </button>
                </form>
              </>
            )}

            {/* Step 3: Success Screen */}
            {step === 3 && (
              <div style={{ textAlign: "center", padding: "12px 0" }}>
                <div className="login-success-icon-badge">
                  <FontAwesomeIcon icon={faCheckCircle} />
                </div>

                <h2 className="login-main-title" style={{ fontSize: "24px", marginBottom: "10px" }}>
                  {success.includes("sent") ? "Reset Link Sent" : "Password Reset Successfully"}
                </h2>

                <p className="signup-desc-text" style={{ marginBottom: "26px", lineHeight: "1.5" }}>
                  {success}
                </p>

                <div style={{ display: "flex", flexDirection: "column", gap: "12px", width: "100%" }}>
                  <button
                    type="button"
                    onClick={handleBackToLogin}
                    className="login-action-btn"
                  >
                    Back to Login
                  </button>

                  {step === 3 && success.includes("sent") && (
                    <button
                      type="button"
                      onClick={handleResetAnother}
                      className="login-secondary-btn"
                    >
                      Send to another email
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Side: Showcase */}
        <div className="login-showcase-side">
          <div className="login-showcase-container">
            <div className="login-showcase-art">
              <img
                src={projectProgressImg}
                alt="Account Security & Recovery"
                className="login-showcase-illustration"
                style={{ borderRadius: "20px" }}
              />
            </div>

            <h2 className="login-showcase-title">{showcaseSlides[activeSlide].title}</h2>
            <p className="login-showcase-desc">{showcaseSlides[activeSlide].description}</p>

            {/* Carousel dots / pills */}
            <div className="login-showcase-pills">
              {showcaseSlides.map((_, idx) => (
                <button
                  key={idx}
                  type="button"
                  className={`login-showcase-pill-btn ${activeSlide === idx ? "active" : ""}`}
                  onClick={() => setActiveSlide(idx)}
                  aria-label={`Showcase slide ${idx + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;